import React, { useEffect, useMemo } from 'react';
import { Modal, Form, Select, InputNumber, Input, Button, Typography, App, Space } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useRegisterWasteMutation } from '../../services/inventoryApi';
import type { InventoryItem } from '../../types/inventory';
import { useResponsive } from '../../hooks/useResponsive';

const { Text } = Typography;

// Catálogo fijo de motivos: alimenta el reporte de mermas por motivo.
// "Otro" existe a propósito para no bloquear el registro en piso.
export const WASTE_REASONS = [
  'Caducidad',
  'Producto dañado',
  'Error de preparación',
  'Devolución de cliente',
  'Robo o faltante',
  'Otro',
];

interface WasteLineForm {
  inventoryItemId?: string;
  quantity?: number;
  reason?: string;
  notes?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  items: InventoryItem[];
  /** Prefill cuando se abre desde la fila de un insumo */
  initialItemId?: string;
}

const WasteRegisterModal: React.FC<Props> = ({ open, onClose, items, initialItemId }) => {
  const { message: messageApi } = App.useApp();
  const { isMobile } = useResponsive();
  const [form] = Form.useForm();
  const [registerWaste, { isLoading }] = useRegisterWasteMutation();

  const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        lines: [{ inventoryItemId: initialItemId, quantity: undefined, reason: undefined, notes: undefined }],
      });
    } else {
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialItemId]);

  // Costo estimado en vivo para que el usuario vea cuánto dinero se va
  const lines: WasteLineForm[] = Form.useWatch('lines', form) || [];
  const estimatedCost = lines.reduce((sum, line) => {
    const item = line?.inventoryItemId ? itemsById.get(line.inventoryItemId) : undefined;
    return sum + (item && line?.quantity ? item.costPerUnit * line.quantity : 0);
  }, 0);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await registerWaste({
        lines: values.lines.map((l: WasteLineForm) => ({
          inventoryItemId: l.inventoryItemId!,
          quantity: l.quantity!,
          reason: l.reason!,
          notes: l.notes || undefined,
        })),
      }).unwrap();
      messageApi.success('Merma registrada');
      onClose();
    } catch (error: any) {
      if (error?.errorFields) return;
      messageApi.error(error?.data?.message || 'Error al registrar la merma');
    }
  };

  return (
    <Modal
      title="Registrar Merma"
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      okText="Registrar"
      cancelText="Cancelar"
      confirmLoading={isLoading}
      width={isMobile ? '100%' : 640}
      destroyOnClose
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        El stock se descuenta y cada línea queda valorizada al costo actual del insumo.
      </Text>
      <Form form={form} layout="vertical">
        <Form.List name="lines">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => {
                const line = lines[field.name];
                const item = line?.inventoryItemId ? itemsById.get(line.inventoryItemId) : undefined;
                return (
                  <div
                    key={field.key}
                    style={{
                      border: '1px solid var(--border-color)',
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 12,
                    }}
                  >
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Form.Item
                        name={[field.name, 'inventoryItemId']}
                        label="Insumo"
                        rules={[{ required: true, message: 'Selecciona el insumo' }]}
                        style={{ flex: 2, marginBottom: 8 }}
                      >
                        <Select
                          showSearch
                          optionFilterProp="children"
                          placeholder="Selecciona un insumo"
                        >
                          {items.filter((i) => i.isActive).map((i) => (
                            <Select.Option key={i.id} value={i.id}>
                              {i.isPreparation ? `${i.name} (preparación)` : i.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'quantity']}
                        label={`Cantidad${item ? ` (${item.unit})` : ''}`}
                        rules={[
                          { required: true, message: 'Indica la cantidad' },
                          { type: 'number', min: 0.0001, message: 'Debe ser mayor a cero' },
                        ]}
                        style={{ flex: 1, marginBottom: 8 }}
                      >
                        <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                      </Form.Item>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                      <Form.Item
                        name={[field.name, 'reason']}
                        label="Motivo"
                        rules={[{ required: true, message: 'Selecciona el motivo' }]}
                        style={{ flex: 1, marginBottom: 4 }}
                      >
                        <Select placeholder="Motivo">
                          {WASTE_REASONS.map((r) => (
                            <Select.Option key={r} value={r}>
                              {r}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'notes']}
                        label="Notas"
                        style={{ flex: 2, marginBottom: 4 }}
                      >
                        <Input placeholder="Opcional" maxLength={500} />
                      </Form.Item>
                      {fields.length > 1 && (
                        <Button
                          icon={<DeleteOutlined />}
                          danger
                          onClick={() => remove(field.name)}
                          style={{ marginBottom: 4 }}
                        />
                      )}
                    </div>
                    {item && line?.quantity ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Costo estimado: ${(item.costPerUnit * line.quantity).toFixed(2)}
                        {item.isPreparation && ' · se descuenta de sus ingredientes'}
                      </Text>
                    ) : null}
                  </div>
                );
              })}
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Button icon={<PlusOutlined />} onClick={() => add({})}>
                  Agregar insumo
                </Button>
                {estimatedCost > 0 && (
                  <Text strong style={{ color: 'var(--color-error-text)' }}>
                    Pérdida total: ${estimatedCost.toFixed(2)}
                  </Text>
                )}
              </Space>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export default WasteRegisterModal;
